import { Component, ViewChild, ViewEncapsulation, inject, input, signal } from "@angular/core";
import { ConfirmationService, PrimeTemplate } from "primeng/api";
import { Router, ActivatedRoute, RouterLink } from "@angular/router";
import { AbstractListController } from "src/app/common/abstract-list.component";
import { Bookings } from "src/app/models/bookings";
import { BookingsService } from "src/app/services/bookings.service";
import { ClientsService } from "src/app/services/clients.service";
import { Clients } from "src/app/models/clients";
import { PARAM_DASHBOARD_PATH } from "../constants/constants";
import { RequestsService } from "src/app/services/requets.service";
import { TopNavComponent } from "../top-nav/top-nav.component";
import { Bind } from "primeng/bind";
import { Card } from "primeng/card";
import { CurrencyPipe, DatePipe } from "@angular/common";
import { Tag } from "primeng/tag";
import { Ripple } from "primeng/ripple";
import { TableModule } from "primeng/table";
import { ButtonDirective } from "primeng/button";
import { Message } from "primeng/message";
import { Drawer } from "primeng/drawer";
import { BookingDetailComponent } from "../components/booking-detail/booking-detail.component";
import { OnlyCalendarComponent } from "../components/calendar/calendar.component";
import { TranslatePipe } from "@ngx-translate/core";

import { TPipe } from "../i18n/t.pipe";
@Component({
    standalone: true,
    selector: "app-admin-dashboard",
    templateUrl: "./admin-dashboard.component.html",
    styleUrl: "./admin-dashboard.component.less",
    encapsulation: ViewEncapsulation.None,
    imports: [TPipe, TopNavComponent, Bind, Card, Tag, Ripple, PrimeTemplate, TableModule, ButtonDirective, RouterLink, Message, Drawer, BookingDetailComponent, OnlyCalendarComponent, CurrencyPipe, DatePipe, TranslatePipe]
})
export class AdminDashboardComponent extends AbstractListController<
  Bookings,
  any
> {
  protected bookingsService: BookingsService;
  private clientService = inject(ClientsService);
  protected confirmationService: ConfirmationService;
  protected requestsService = inject(RequestsService);

  title = "Prenotazioni";
  readonly events = input<{
    title: string;
    start: Date;
    end: Date;
}[]>([]);
  readonly daysOfWeek = signal<string[]>([
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ]);
  readonly timeSlots = signal<string[]>([]);
  readonly gridEvents = signal<any>({});
  currentDate: Date = new Date();
  readonly displayedWeek = signal<string>("");

  readonly calendarDialog = signal<boolean>(false);
  readonly cols = signal<any[]>(undefined);
  readonly columnOptions = signal<any[]>(undefined);
  readonly selectedColumns = signal<any[]>(undefined);
  readonly bookedDialog = signal<boolean>(false);
  readonly requestDialog = signal<boolean>(false);
  readonly booking = signal<Bookings>(undefined);
  readonly bookings = signal<Bookings[]>([]);
  readonly client = signal<Clients>(undefined);
  readonly selectedWorks = signal<any[]>(undefined);
  readonly requests = signal<Bookings[]>([]);
  readonly weekBookings = signal<Bookings[]>([]);
  readonly kpis = signal({ arrivals: 0, departures: 0, inHouse: 0, pending: 0 });

  constructor() {
    const bookingsService = inject(BookingsService);
    const confirmationService = inject(ConfirmationService);
    const router = inject(Router);
    const route = inject(ActivatedRoute);

    super(
      bookingsService,
      confirmationService,
      router,
      route,
      PARAM_DASHBOARD_PATH
    );

    this.bookingsService = bookingsService;
    this.confirmationService = confirmationService;
  }

  ngOnInit(): void {
    this.loadRequests();

    this.generateTimeSlots();
    this.updateWeekDays();
    this.mapEventsToGrid();
    this.loadBookings();
    this.loadWeek();
    this.loadKpis();
  }

  todayLabel(): string {
    return new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  }

  weekdayName(day: string): string {
    const [d, m, y] = day.split(".").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("it-IT", { weekday: "short" });
  }

  private total(params: Record<string, string | number>, set: (n: number) => void): void {
    this.bookingsService.list({ _start: 1, _limit: 1, ...params }).subscribe(() => set(this.bookingsService.size));
  }

  loadKpis(): void {
    const today = this.formatDate(new Date());
    const patch = (key: "arrivals" | "departures" | "inHouse" | "pending") => (n: number) =>
      this.kpis.update((k) => ({ ...k, [key]: n }));
    this.total({ is_booked: "1", date_from: today }, patch("arrivals"));
    this.total({ is_booked: "1", date_to: today }, patch("departures"));
    this.total({ is_booked: "1", date_from_lte: today, date_to_gt: today }, patch("inHouse"));
    this.total({ is_booked: "0" }, patch("pending"));
  }

  loadWeek(): void {
    this.bookingsService
      .list({
        _start: 1,
        _limit: 100,
        is_booked: "1",
        date_from_gte: this.formatDate(this.getStartOfWeek(this.currentDate)),
        date_from_lte: this.formatDate(this.getEndOfWeek(this.currentDate)),
      })
      .subscribe((pData) => {
        this.weekBookings.set(pData);
        this.mapEventsToGrid();
      });
  }

  thisWeek(): void {
    this.currentDate = new Date();
    this.updateWeekDays();
    this.loadWeek();
  }

  loadBookings() {
    this.bookingsService.list({ _start: 1, _limit: 5, is_booked: "1" }).subscribe((pData) => {
      this.bookings.set(pData);
    });
  }

  mapEventsToGrid(): void {
    this.gridEvents.set(this.daysOfWeek().reduce((acc: any, day) => {
      acc[day] = [];
      return acc;
    }, {}));

    const list = Array.isArray(this.weekBookings()) ? this.weekBookings() : [];

    list.forEach((booking) => {
      const eventDate = new Date(booking.date_from);
      const eventDay = this.formatDay(eventDate);

      if (this.gridEvents()[eventDay]) {
        this.gridEvents()[eventDay].push({
          id: booking.id,
          title: booking.title,
          client: booking.client,
          room: booking.room,
          nights: Math.max(0, Math.round((new Date(booking.date_to).getTime() - eventDate.getTime()) / 86400000)),
        });
      }
    });
  }

  loadRequests() {
    const paramsRequests = {
      _start: 1,
      _limit: 10,
      is_booked: "0",
    };
    this.bookingsService.list(paramsRequests).subscribe((pData) => {
      this.requests.set(pData);
    });
  }

  editRoom(booking: any) {
    this.bookedDialog.set(true);

    const bookingId = booking.id;

    console.log(bookingId);

    this.bookingsService.getId(bookingId).subscribe((res) => {
      this.booking.set(res);
    });
  }

  bookingCalendar() {
    this.calendarDialog.set(true);
  }

  editRequest(booking: any) {
    this.requestDialog.set(true);

    const bookingId = booking.id;

    console.log(bookingId);

    this.bookingsService.getId(bookingId).subscribe((res) => {
      this.booking.set(res);
    });
  }

  getFilterParams(): any {
    return {
      dateFrom: this.filter("dateFrom"),
      dateTo: this.filter("dateTo"),
    };
  }

  applyFilterParams(params: any): void {
    this.setFilter("dateFrom", params["date_from"] || "");
    this.setFilter("dateTo", params["date_to"] || "");
  }

  edit(booking: Bookings) {
    this.booking.set({ ...booking });

    this.clientService.getId(this.booking().client_id).subscribe((res) => {
      this.client.set(res);
    });

    this.selectedWorks.set(this.booking().works_id.split(","));
    this.bookedDialog.set(true);
  }

  eventRender(event) {
    const html = `<div>
      <div class="event-time"><i class="pi pi-clock"></i> ${event.event.time}</div>
      <div class="event-client-name"><strong>${event.event.client.name}</strong><br>${event.event.client.surname}</div>

    </div>`;
    event.element.html(html);
  }

  generateTimeSlots(): void {

    for (let hour = 8; hour <= 20; hour++) {
      this.timeSlots.update((a) => { a.push(`${hour}:00`); return [...a]; });
      this.timeSlots.update((a) => { a.push(`${hour}:30`); return [...a]; });
    }
  }

  isToday(day: string): boolean {
    const today = new Date();
    const formattedDay = this.formatDay(today);

    return day === formattedDay;
  }

  updateWeekDays(): void {

    const startOfWeek = this.getStartOfWeek(this.currentDate);
    const endOfWeek = this.getEndOfWeek(this.currentDate);

    this.displayedWeek.set(`${this.formatDate(startOfWeek)} - ${this.formatDate(
      endOfWeek
    )}`);

    this.daysOfWeek.set([]);
    let day = new Date(startOfWeek);
    for (let i = 0; i < 7; i++) {
      this.daysOfWeek.update((a) => { a.push(this.formatDay(day)); return [...a]; });
      day.setDate(day.getDate() + 1);
    }
  }

  getStartOfWeek(date: Date): Date {
    const dayOfWeek = date.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const startOfWeek = new Date(date);
    startOfWeek.setDate(date.getDate() + diff);
    return startOfWeek;
  }

  getEndOfWeek(date: Date): Date {
    const startOfWeek = this.getStartOfWeek(date);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    return endOfWeek;
  }

  formatDate(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  formatDay(date: Date): string {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}.${month}.${year}`;
  }

  previousWeek(): void {
    this.currentDate.setDate(this.currentDate.getDate() - 7);
    this.updateWeekDays();
    this.loadWeek();
  }

  nextWeek(): void {
    this.currentDate.setDate(this.currentDate.getDate() + 7);
    this.updateWeekDays();
    this.loadWeek();
  }
}
